import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, ApiError, Button, Card, Eyebrow, Field, Input } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT, type StringKey } from '../i18n/index.js';

/**
 * Your own account.
 *
 * Reached from the account menu at the foot of the side nav, which is where somebody looks
 * for their own settings — and, since language moved to the header, is now the only thing
 * that menu is for.
 *
 * Everything here is scoped to the signed-in account by the API, which takes no id in the
 * path. There is nothing on this screen that could be pointed at somebody else.
 */
export function Profile() {
  const t = useT();
  const { user, refreshUser } = useSession();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  /**
   * The message is held as a KEY, not as translated text.
   *
   * Storing the sentence meant it froze in whatever language was current when it was
   * produced: save in English, switch to French, and the console was French around an
   * English notice. Keys are translated at render, so the message follows the language
   * like everything else on the page.
   */
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; key: StringKey } | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Seeded from the session rather than fetched again: the shell already loaded this
  // account, and a second request would only race it.
  useEffect(() => {
    setName(user?.name ?? '');
    setPhone(user?.phone ?? '');
  }, [user?.name, user?.phone]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      setDetail(null);
      await api.patch('/platform/auth/profile', { name: name.trim(), phone: phone.trim() });
      await refreshUser();
      setMessage({ tone: 'success', key: 'profile.saved' });
    } catch (error) {
      // The server's own words when it has any — a validation message says more than
      // "could not save that" — but the fallback is still a key.
      setDetail(error instanceof ApiError ? error.message : null);
      setMessage({ tone: 'error', key: 'profile.saveFailed' });
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    setUploading(true);
    setMessage(null);
    try {
      const body = new FormData();
      body.append('file', file);
      // No content-type header: the browser has to set the multipart boundary itself, and
      // naming the type here is what stops it doing so.
      setDetail(null);
      await api.request('/platform/auth/profile/avatar', { method: 'POST', body });
      await refreshUser();
      setMessage({ tone: 'success', key: 'profile.saved' });
    } catch (error) {
      setDetail(error instanceof ApiError ? error.message : null);
      setMessage({ tone: 'error', key: 'profile.photoFailed' });
    } finally {
      setUploading(false);
      // Cleared so choosing the same file twice fires change again — a retry after a
      // failure is otherwise silently ignored.
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removePhoto() {
    setUploading(true);
    setMessage(null);
    try {
      await api.delete('/platform/auth/profile/avatar');
      await refreshUser();
    } catch {
      setDetail(null);
      setMessage({ tone: 'error', key: 'profile.photoFailed' });
    } finally {
      setUploading(false);
    }
  }

  const initials = (user?.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <Page title={t('profile.title')}>
      <div className="max-w-xl">
        {message && (
          <div className="mb-4">
            <Alert tone={message.tone === 'success' ? 'success' : 'error'}>
              {detail ?? t(message.key)}
            </Alert>
          </div>
        )}

        <Card>
          <Eyebrow>{t('profile.photo')}</Eyebrow>
          <div className="mt-3 flex items-center gap-4">
            {/* Initials when there is no picture. A stock silhouette says "this person has
                no face"; initials say "no picture yet", which is what is true. */}
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="h-16 w-16 rounded-full object-cover"
                style={{ boxShadow: 'inset 0 0 0 1px var(--hairline-strong)' }}
              />
            ) : (
              <span
                className="grid h-16 w-16 place-items-center rounded-full text-lg font-bold"
                style={{ background: 'var(--ink-700)', color: 'var(--signal)' }}
                aria-hidden
              >
                {initials || '—'}
              </span>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  loading={uploading}
                  onClick={() => fileInput.current?.click()}
                >
                  {user?.avatarUrl ? t('profile.replace') : t('profile.upload')}
                </Button>
                {user?.avatarUrl && (
                  <Button type="button" variant="ghost" disabled={uploading} onClick={() => void removePhoto()}>
                    {t('profile.removePhoto')}
                  </Button>
                )}
              </div>
              <p className="text-xs" style={{ color: 'var(--chalk-faint)' }}>
                {t('profile.photoHint')}
              </p>
            </div>

            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
              }}
            />
          </div>
        </Card>

        <form onSubmit={save} className="mt-4">
          <Card>
            <Eyebrow>{t('profile.heading')}</Eyebrow>
            <p className="mt-1 text-sm" style={{ color: 'var(--chalk-dim)' }}>
              {t('profile.hint')}
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <Field label={t('profile.name')} htmlFor="pf-name">
                <Input id="pf-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>

              <Field label={t('profile.phone')} htmlFor="pf-phone" hint={t('profile.phoneHint')}>
                <Input id="pf-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </Field>

              {/* Read-only, and it says why. Email is the login identity and half of a
                  unique constraint; changing it without a verification round trip is how
                  somebody locks themselves out or claims an address they do not hold. */}
              <Field label={t('profile.email')} htmlFor="pf-email" hint={t('profile.emailLocked')}>
                <Input id="pf-email" value={user?.email ?? ''} readOnly disabled />
              </Field>

              <div>
                <Button type="submit" loading={busy}>
                  {t('profile.save')}
                </Button>
              </div>
            </div>
          </Card>
        </form>
      </div>
    </Page>
  );
}
